import { useState } from "react";
import api from "../api/client";

/* ── CSS ─────────────────────────────────────────────────── */
const CSS = `
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
:root {
  --bg:      #f0f4fc; --surface: #ffffff; --s2: #f4f8ff;
  --border:  #c0d0eb; --b2: #d5e3f5;
  --accent:  #7c3aed; --text: #0d1a36; --text2: #2a4a78;
  --muted:   #6080aa; --green: #059669; --red: #dc2626; --blue: #0284c7;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  color-scheme: light;
}
body { background: var(--bg); color: var(--text); }

/* Tabs */
.cp-tabs { display:flex; border-bottom:1px solid var(--b2); margin-bottom:28px; }
.cp-tab  { padding:0 20px; height:40px; font-size:12px; font-weight:500; color:var(--muted);
  background:none; border:none; border-bottom:2px solid transparent; margin-bottom:-1px;
  cursor:pointer; transition:color .15s,border-color .15s; letter-spacing:.01em; }
.cp-tab:hover { color:var(--text2); }
.cp-tab.active { color:var(--accent); border-bottom-color:var(--accent); font-weight:600; }

/* Alert */
.cp-alert { padding:11px 16px; border-radius:8px; font-size:13px; margin-bottom:20px;
  display:flex; align-items:center; gap:10px; }
.cp-alert-success { background:rgba(16,185,129,.08); border:1px solid rgba(16,185,129,.2); color:var(--green); }
.cp-alert-error   { background:rgba(248,113,113,.08); border:1px solid rgba(248,113,113,.2); color:var(--red); }

/* Card */
.cp-card { background:var(--surface); border:1px solid var(--border); border-radius:10px; padding:24px; margin-bottom:16px; }
.cp-card-title { font-size:11px; font-weight:700; letter-spacing:.07em; text-transform:uppercase; color:var(--muted); margin-bottom:16px; }

/* Fields */
.cp-row   { display:flex; gap:14px; margin-bottom:20px; flex-wrap:wrap; }
.cp-field { display:flex; flex-direction:column; gap:5px; flex:1; min-width:200px; }
.cp-label { font-size:11px; font-weight:600; letter-spacing:.06em; text-transform:uppercase; color:var(--muted); }
.cp-input { background:var(--s2); border:1px solid var(--b2); border-radius:7px;
  padding:9px 12px; font-size:13px; color:var(--text); outline:none; font-family:inherit;
  transition:border-color .15s; }
.cp-input:focus { border-color:var(--accent); }

/* Drop zones */
.cp-zones { display:grid; grid-template-columns:1fr 1fr 1fr; gap:14px; margin-bottom:20px; }
@media(max-width:680px){ .cp-zones{ grid-template-columns:1fr; } }
.cp-zone { border:2px dashed var(--border); border-radius:8px; padding:16px; text-align:center;
  cursor:pointer; transition:border-color .2s,background .2s; background:var(--s2); }
.cp-zone.drag,.cp-zone:hover { border-color:var(--accent); background:#f4f8ff; }
.cp-zone-label { font-size:11px; font-weight:600; letter-spacing:.06em; text-transform:uppercase;
  color:var(--accent); margin-bottom:8px; }
.cp-zone-hint  { font-size:11px; color:var(--muted); }
.cp-zone-files { margin-top:8px; text-align:left; }
.cp-file-row   { display:flex; align-items:center; gap:6px; padding:3px 0; }
.cp-file-name  { font-size:11px; color:var(--text2); flex:1; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.cp-file-del   { background:none; border:none; color:var(--red); cursor:pointer; font-size:14px; line-height:1; flex-shrink:0; opacity:.7; }
.cp-file-del:hover { opacity:1; }

/* Buttons */
.cp-btn { display:inline-flex; align-items:center; gap:7px;
  padding:9px 20px; border-radius:7px; border:none; cursor:pointer;
  font-size:13px; font-weight:600; letter-spacing:.01em; transition:opacity .15s; }
.cp-btn:disabled { opacity:.4; cursor:not-allowed; }
.cp-btn:not(:disabled):hover { opacity:.88; }
.cp-btn-primary { background:var(--accent); color:#fff; }
.cp-btn-success { background:var(--green); color:#f0fdf4; }
.cp-btn-ghost   { background:none; border:1px solid var(--border); color:var(--text2); }
.cp-btn-danger  { background:none; border:1px solid rgba(248,113,113,.3); color:var(--red); }
.cp-spinner { display:inline-block; width:14px; height:14px; border:2px solid rgba(255,255,255,.2);
  border-top-color:#fff; border-radius:50%; animation:spin .6s linear infinite; flex-shrink:0; }
@keyframes spin { to{ transform:rotate(360deg); } }

.cp-actions { display:flex; gap:10px; align-items:center; flex-wrap:wrap; }
.cp-file-count { font-size:12px; color:var(--muted); flex:1; }

/* Result box */
.cp-result { background:var(--s2); border:1px solid var(--border); border-radius:10px;
  padding:24px; margin-bottom:16px; max-height:70vh; overflow-y:auto; }
.cp-result-header { display:flex; justify-content:space-between; align-items:center;
  margin-bottom:20px; padding-bottom:16px; border-bottom:1px solid var(--b2); flex-wrap:wrap; gap:10px; }
.cp-result-title { font-size:14px; font-weight:700; color:var(--text); }

/* Markdown rendered output */
.cp-md { font-size:13px; color:var(--text2); line-height:1.7; }
.cp-md-h1 { font-size:15px; font-weight:700; color:var(--blue); margin:20px 0 8px; padding-bottom:6px; border-bottom:1px solid var(--b2); }
.cp-md-h2 { font-size:13px; font-weight:700; color:var(--accent); margin:16px 0 6px; text-transform:uppercase; letter-spacing:.05em; }
.cp-md-bullet { display:flex; gap:8px; margin:3px 0 3px 8px; }
.cp-md-bullet::before { content:"•"; color:var(--accent); flex-shrink:0; }
.cp-md-sub-bullet { display:flex; gap:8px; margin:2px 0 2px 24px; color:var(--text2); }
.cp-md-sub-bullet::before { content:"–"; color:var(--muted); flex-shrink:0; }
.cp-md-p { margin:4px 0; }
.cp-md strong { color:var(--text); font-weight:600; }

/* Table */
.cp-table-wrap { overflow-x:auto; margin:12px 0; border-radius:8px; border:1px solid var(--border); }
.cp-table { width:100%; border-collapse:collapse; font-size:12px; }
.cp-table th { background:var(--surface); color:var(--text); font-weight:600; padding:9px 14px;
  text-align:left; border-bottom:2px solid var(--border); white-space:nowrap; }
.cp-table td { padding:8px 14px; border-bottom:1px solid var(--b2); color:var(--text2); vertical-align:top; }
.cp-table tr:last-child td { border-bottom:none; }
.cp-table tr:nth-child(even) td { background:rgba(255,255,255,.02); }
.cp-td-compliant  { color:var(--green) !important; font-weight:600; }
.cp-td-gap        { color:var(--red) !important; font-weight:600; }
.cp-td-medium     { color:#b45309 !important; font-weight:600; }

/* Saved analyses */
.cp-analysis-card { background:var(--surface); border:1px solid var(--border);
  border-radius:10px; margin-bottom:10px; overflow:hidden; }
.cp-analysis-header { display:flex; align-items:center; gap:12px; padding:14px 18px; cursor:pointer; }
.cp-analysis-chevron { font-size:12px; color:var(--muted); transition:transform .2s; flex-shrink:0; }
.cp-analysis-chevron.open { transform:rotate(180deg); }
.cp-analysis-title { font-size:14px; font-weight:600; flex:1; }
.cp-analysis-meta  { font-size:11px; color:var(--muted); margin-top:2px; }
.cp-analysis-body  { padding:0 18px 20px; border-top:1px solid var(--b2); }
.cp-empty { text-align:center; padding:60px 20px; color:var(--muted); font-size:13px; }
.cp-empty-icon { font-size:40px; margin-bottom:12px; opacity:.5; }
`;

/* ── Markdown renderer ───────────────────────────────────── */
function renderInline(text) {
  // Bold: **text**
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={i}>{p.slice(2, -2)}</strong>
      : p
  );
}

function parseTable(lines) {
  const rows = lines
    .filter(l => !l.match(/^\|[-| ]+\|$/))  // skip separator rows
    .map(l => l.replace(/^\||\|$/g, "").split("|").map(c => c.trim()));
  if (rows.length < 2) return null;
  const [header, ...body] = rows;

  const statusClass = (val) => {
    const v = val.toLowerCase();
    if (v.includes("compliant") && !v.includes("non")) return "cp-td-compliant";
    if (v.includes("non-compliant") || v.includes("gap") || v.includes("missing")) return "cp-td-gap";
    if (v.includes("medium") || v.includes("partial")) return "cp-td-medium";
    return "";
  };

  return (
    <div className="cp-table-wrap">
      <table className="cp-table">
        <thead>
          <tr>{header.map((h, i) => <th key={i}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {body.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => (
                <td key={ci} className={statusClass(cell)}>{renderInline(cell)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MarkdownResult({ text }) {
  const lines = text.split("\n");
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Table block
    if (line.startsWith("|")) {
      const tableLines = [];
      while (i < lines.length && lines[i].startsWith("|")) {
        tableLines.push(lines[i]); i++;
      }
      const tbl = parseTable(tableLines);
      if (tbl) elements.push(<div key={`t${i}`}>{tbl}</div>);
      continue;
    }

    // Section header: **1. Title** or ## Title or **Title**
    if (/^\*\*\d+\.\s/.test(line) || /^#{1,3}\s/.test(line)) {
      const clean = line.replace(/^\*\*|\*\*$/g,"").replace(/^#{1,3}\s/,"").trim();
      elements.push(<div key={i} className="cp-md-h1">{clean}</div>);
      i++; continue;
    }

    // Sub-bold header: **Word:** (short bold labels)
    if (/^\*\*[^*]+\*\*:?\s*$/.test(line.trim())) {
      const clean = line.replace(/\*\*/g,"").trim();
      elements.push(<div key={i} className="cp-md-h2">{clean}</div>);
      i++; continue;
    }

    // Bullet
    if (/^[-*•]\s/.test(line)) {
      elements.push(
        <div key={i} className="cp-md-bullet">{renderInline(line.replace(/^[-*•]\s/,""))}</div>
      );
      i++; continue;
    }

    // Sub-bullet (indented)
    if (/^\s{2,}[-*]\s/.test(line)) {
      elements.push(
        <div key={i} className="cp-md-sub-bullet">{renderInline(line.trim().replace(/^[-*]\s/,""))}</div>
      );
      i++; continue;
    }

    // Empty line
    if (!line.trim()) { elements.push(<div key={i} style={{height:8}} />); i++; continue; }

    // Regular paragraph
    elements.push(<div key={i} className="cp-md-p">{renderInline(line)}</div>);
    i++;
  }

  return <div className="cp-md">{elements}</div>;
}

/* ── Drop Zone ───────────────────────────────────────────── */
function DropZone({ label, accent, files, setFiles }) {
  const [drag, setDrag] = useState(false);
  const id = `dz-${label.replace(/\s/g,"_")}`;
  const add = fs => setFiles(prev => [...prev, ...Array.from(fs)].slice(0, 10));

  return (
    <div>
      <div className={`cp-zone${drag?" drag":""}`}
           onDragOver={e=>{e.preventDefault();setDrag(true);}}
           onDragLeave={()=>setDrag(false)}
           onDrop={e=>{e.preventDefault();setDrag(false);add(e.dataTransfer.files);}}
           onClick={()=>document.getElementById(id).click()}>
        <div className="cp-zone-label" style={{color:accent}}>{label}</div>
        <div className="cp-zone-hint">Drop files or click to browse</div>
        <div className="cp-zone-hint" style={{marginTop:4,fontSize:10}}>PDF · Word · Excel · Images</div>
        <input id={id} type="file" hidden multiple
               accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
               onChange={e=>add(e.target.files)} />
      </div>
      {files.length > 0 && (
        <div className="cp-zone-files">
          {files.map((f,i) => (
            <div className="cp-file-row" key={i}>
              <span className="cp-file-name">📄 {f.name}</span>
              <button className="cp-file-del" onClick={()=>setFiles(p=>p.filter((_,j)=>j!==i))}>×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Saved Analysis Card ─────────────────────────────────── */
function AnalysisCard({ record, onDelete }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="cp-analysis-card">
      <div className="cp-analysis-header" onClick={() => setOpen(o => !o)}>
        <span className={`cp-analysis-chevron${open?" open":""}`}>▼</span>
        <div style={{flex:1,minWidth:0}}>
          <div className="cp-analysis-title">{record.bid_title}</div>
          <div className="cp-analysis-meta">{record.customer} · {record.analysis_date} · {record.created_by}</div>
        </div>
        <button className="cp-btn cp-btn-danger" style={{padding:'5px 12px',fontSize:11}}
                onClick={e=>{e.stopPropagation();onDelete(record.id);}}>Delete</button>
      </div>
      {open && (
        <div className="cp-analysis-body">
          <MarkdownResult text={record.result} />
        </div>
      )}
    </div>
  );
}

/* ── Main ────────────────────────────────────────────────── */
export default function Compliance() {
  const [tab, setTab]           = useState(0);
  const [alert, setAlert]       = useState({ msg:"", type:"" });

  const [bidTitle, setBidTitle] = useState("");
  const [customer, setCustomer] = useState("");
  const [rfqFiles, setRfqFiles] = useState([]);
  const [supFiles, setSupFiles] = useState([]);
  const [cliFiles, setCliFiles] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult]     = useState("");
  const [saving, setSaving]     = useState(false);

  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [loaded, setLoaded]     = useState(false);

  const notify = (msg, type="success") => {
    setAlert({ msg, type });
    setTimeout(() => setAlert({ msg:"", type:"" }), 5000);
  };

  const allFiles = [...rfqFiles, ...supFiles, ...cliFiles];

  // PDFs/Word/Excel docs can't be re-compressed like photos, so the only
  // real safeguard here is warning before hitting a proxy size limit rather
  // than after. 8MB is a conservative guess at the ingress's actual limit —
  // adjust this if/once the real limit is confirmed or raised.
  const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;
  const totalBytes = allFiles.reduce((sum, f) => sum + f.size, 0);

  const handleAnalyze = async () => {
    if (!bidTitle || !customer || allFiles.length === 0) return;
    if (totalBytes > MAX_UPLOAD_BYTES) {
      notify(
        `These ${allFiles.length} files total ${(totalBytes/1024/1024).toFixed(1)}MB, which is likely too large to upload together — try fewer or smaller files.`,
        "error"
      );
      return;
    }
    setAnalyzing(true); setResult("");
    try {
      const fd = new FormData();
      fd.append("bid_title", bidTitle);
      fd.append("customer", customer);
      allFiles.forEach(f => fd.append("files", f));
      const res = await api.post("/compliance/analyze", fd);
      setResult(res.data.result);
    } catch (e) {
      let msg = "Analysis failed";
      if (e.response) {
        msg = e.response.status === 413
          ? "These files are too large to upload together — try fewer or smaller files."
          : (e.response.data?.detail || "Analysis failed");
      } else if (e.request) {
        msg = "Upload failed — this usually means the files are too large together, or there's a connection issue. Try fewer/smaller files.";
      }
      notify(msg, "error");
    } finally { setAnalyzing(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post("/compliance/analyses", { bid_title: bidTitle, customer, result });
      notify("Analysis saved successfully.");
      setResult(""); setBidTitle(""); setCustomer("");
      setRfqFiles([]); setSupFiles([]); setCliFiles([]);
    } catch { notify("Save failed", "error"); }
    finally { setSaving(false); }
  };

  const loadAnalyses = async () => {
    setLoading(true);
    const res = await api.get("/compliance/analyses");
    setAnalyses(res.data); setLoaded(true); setLoading(false);
  };

  const deleteAnalysis = async (id) => {
    await api.delete(`/compliance/analyses/${id}`);
    setAnalyses(p => p.filter(a => a.id !== id));
    notify("Deleted.");
  };

  return (
    <>
      <style>{CSS}</style>

      <div className="cp-tabs">
        {["📋 New Analysis","📂 Saved Analyses"].map((label, i) => (
          <button key={i} className={`cp-tab${tab===i?" active":""}`}
                  onClick={() => { setTab(i); if (i===1 && !loaded) loadAnalyses(); }}>
            {label}
          </button>
        ))}
      </div>

      {alert.msg && (
        <div className={`cp-alert cp-alert-${alert.type}`}>
          {alert.type==="success"?"✓":"⚠"} {alert.msg}
        </div>
      )}

      {/* ── TAB 0: NEW ANALYSIS ── */}
      {tab === 0 && (
        <div>
          <div className="cp-card">
            <div className="cp-card-title">Bid Information</div>
            <div className="cp-row">
              <div className="cp-field">
                <label className="cp-label">Bid Title *</label>
                <input className="cp-input" value={bidTitle} onChange={e=>setBidTitle(e.target.value)}
                       placeholder="e.g. Network Equipment Supply — 2025" />
              </div>
              <div className="cp-field">
                <label className="cp-label">Customer *</label>
                <input className="cp-input" value={customer} onChange={e=>setCustomer(e.target.value)}
                       placeholder="e.g. Ministry of Health" />
              </div>
            </div>

            <div className="cp-card-title">Upload Documents</div>
            <div className="cp-zones">
              <DropZone label="📄 RFQ / Tender Documents" accent="#2563eb" files={rfqFiles} setFiles={setRfqFiles} />
              <DropZone label="🏭 Supplier Quotes"         accent="#b45309" files={supFiles} setFiles={setSupFiles} />
              <DropZone label="📤 Client Quotation"        accent="#059669" files={cliFiles} setFiles={setCliFiles} />
            </div>

            <div className="cp-actions">
              <span className="cp-file-count">{allFiles.length} / 10 files</span>
              <button className="cp-btn cp-btn-primary" onClick={handleAnalyze}
                      disabled={analyzing||!bidTitle||!customer||allFiles.length===0||allFiles.length>10}>
                {analyzing ? <><span className="cp-spinner"/>Analyzing…</> : "🔍 Analyze Bid"}
              </button>
            </div>
          </div>

          {/* Result */}
          {result && (
            <div className="cp-card" style={{padding:0,overflow:'hidden'}}>
              <div className="cp-result-header" style={{padding:'20px 24px 16px'}}>
                <div className="cp-result-title">📊 Compliance Analysis Report</div>
                <div className="cp-actions">
                  <button className="cp-btn cp-btn-success" onClick={handleSave} disabled={saving}>
                    {saving ? <><span className="cp-spinner"/>Saving…</> : "💾 Save Report"}
                  </button>
                  <button className="cp-btn cp-btn-ghost" onClick={()=>setResult("")}>Discard</button>
                </div>
              </div>
              <div style={{padding:'0 24px 24px',maxHeight:'65vh',overflowY:'auto'}}>
                <MarkdownResult text={result} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── TAB 1: SAVED ANALYSES ── */}
      {tab === 1 && (
        <div>
          <div style={{marginBottom:20}}>
            <button className="cp-btn cp-btn-primary" onClick={loadAnalyses} disabled={loading}>
              {loading ? <><span className="cp-spinner"/>Loading…</> : "↻ Refresh"}
            </button>
          </div>
          {loaded && analyses.length === 0 && (
            <div className="cp-empty">
              <div className="cp-empty-icon">📂</div>
              No saved analyses yet.
            </div>
          )}
          {analyses.map(a => (
            <AnalysisCard key={a.id} record={a} onDelete={deleteAnalysis} />
          ))}
        </div>
      )}
    </>
  );
}
